import { useState } from 'react';
import type { TimeEntry } from '../utils/dayLegs';
import { spanMinutes } from '../utils/dayLegs';
import { formatCompactMinutes } from '../utils/visitProjectTime';

/**
 * The day's clock-in/out windows, with the corrections a rep needs when a
 * punch didn't happen the way the day did (#3452): clocked in late, forgot to
 * clock out, or worked a stretch with the app closed.
 *
 * Times are edited as plain `HH:MM` on the window's own date, so a correction
 * can never silently land on a different day.
 */

interface ClockPunchesProps {
  windows: TimeEntry[];
  onCorrect: (legId: string, which: 'in' | 'out', at: Date | null) => void;
  /** Remove a stretch punched by mistake. Refuses, with a reason, when work sits inside it. */
  onDelete: (legId: string) => { ok: true } | { ok: false; reason: string };
}

function toTimeInput(date: Date): string {
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
}

function formatClock(date: Date): string {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Apply an `HH:MM` value to the same calendar day as `sameDayAs`. */
function withTime(sameDayAs: Date, value: string): Date | null {
  const [h, m] = value.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const next = new Date(sameDayAs);
  next.setHours(h, m, 0, 0);
  return next;
}

export default function ClockPunches({ windows, onCorrect, onDelete }: ClockPunchesProps) {
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ legId: string; which: 'in' | 'out' } | null>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const beginEdit = (leg: TimeEntry, which: 'in' | 'out') => {
    const current = which === 'in' ? leg.startTime : leg.endTime;
    setDraft(toTimeInput(current ?? new Date()));
    setEditing({ legId: leg.id, which });
    setError(null);
  };

  const saveEdit = (leg: TimeEntry) => {
    if (!editing) return;
    let next = withTime(leg.startTime, draft);
    if (!next) {
      setError('Enter a time as HH:MM.');
      return;
    }
    // A clock-out earlier in the day than the clock-in means the shift ran
    // past midnight — the overnight case this redesign exists to support.
    // Without this the rep is told the clock-out must be after the clock-in
    // and has no way to satisfy it, so the window stays open forever.
    if (editing.which === 'out' && next <= leg.startTime) {
      next = new Date(next.getTime() + 24 * 60 * 60 * 1000);
    }
    // A window has to run forwards. Anything else would produce a negative
    // span that the derivation clamps to zero, silently losing the rep's time.
    if (editing.which === 'in' && leg.endTime && next >= leg.endTime) {
      setError('Clock-in has to be before clock-out.');
      return;
    }
    onCorrect(leg.id, editing.which, next);
    setEditing(null);
    setError(null);
  };

  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow-card">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-800">Clock In / Out</h3>
      </div>

      {windows.length === 0 && (
        <p className="text-sm text-slate-500">
          No clock-in yet today. Missed work gets recorded with Add time below — the clock
          follows the visit.
        </p>
      )}

      <div className="space-y-2">
        {windows.map((leg) => {
          const isEditing = editing?.legId === leg.id;
          const minutes = leg.endTime ? spanMinutes(leg.startTime, leg.endTime) : null;
          return (
            <div key={leg.id} className="rounded-lg border border-slate-200 p-3" data-testid="clock-window">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  {isEditing && editing.which === 'in' ? (
                    <input
                      type="time"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      aria-label="Clock-in time"
                      className="rounded border border-slate-300 px-2 py-1"
                      autoFocus
                    />
                  ) : (
                    <button
                      onClick={() => beginEdit(leg, 'in')}
                      className="font-medium text-primary-600"
                      aria-label={`Edit clock-in ${formatClock(leg.startTime)}`}
                    >
                      {formatClock(leg.startTime)}
                    </button>
                  )}
                  <span className="text-slate-400">→</span>
                  {isEditing && editing.which === 'out' ? (
                    <input
                      type="time"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      aria-label="Clock-out time"
                      className="rounded border border-slate-300 px-2 py-1"
                      autoFocus
                    />
                  ) : leg.endTime ? (
                    <button
                      onClick={() => beginEdit(leg, 'out')}
                      className="font-medium text-primary-600"
                      aria-label={`Edit clock-out ${formatClock(leg.endTime)}`}
                    >
                      {formatClock(leg.endTime)}
                    </button>
                  ) : (
                    <button
                      onClick={() => beginEdit(leg, 'out')}
                      className="rounded bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800"
                    >
                      Still open — set clock-out
                    </button>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {minutes !== null && !isEditing && (
                    <span className="text-sm text-slate-500">{formatCompactMinutes(minutes)}</span>
                  )}
                  {!isEditing && (
                    <button
                      onClick={() => { setConfirmingDelete(leg.id); setError(null); }}
                      title="Delete this stretch"
                      aria-label={`Delete stretch starting ${formatClock(leg.startTime)}`}
                      data-testid="delete-clock-window"
                      className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {confirmingDelete === leg.id && (
                <div className="mt-2 rounded-lg bg-red-50 p-2 text-sm">
                  <p className="text-red-800">Delete this stretch? The correction is kept on your record.</p>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      onClick={() => {
                        const res = onDelete(leg.id);
                        setConfirmingDelete(null);
                        if (!res.ok) setError(res.reason);
                      }}
                      className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white"
                      data-testid="confirm-delete-clock-window"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setConfirmingDelete(null)}
                      className="px-2 py-1.5 text-sm text-slate-500"
                    >
                      Keep
                    </button>
                  </div>
                </div>
              )}

              {isEditing && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => saveEdit(leg)}
                    className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => { setEditing(null); setError(null); }}
                    className="px-2 py-1.5 text-sm text-slate-500"
                  >
                    Cancel
                  </button>
                  {editing.which === 'out' && leg.endTime && (
                    <button
                      onClick={() => { onCorrect(leg.id, 'out', null); setEditing(null); }}
                      className="ml-auto text-sm text-slate-500 underline"
                    >
                      Reopen
                    </button>
                  )}
                </div>
              )}

              {leg.manual && !isEditing && (
                <p className="mt-1 text-xs text-slate-400">Entered by hand</p>
              )}
            </div>
          );
        })}
      </div>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
